import React, {useEffect, useMemo, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from '@material-ui/core';
import _ from 'lodash';
import ToolBar from '../toolbar/ToolBar';
import Message from '../message/Message';
import CardCustomizado from '../cardcustomizado/CardCustomizado';
import MobilePagination from '../paginationactions/MobilePagination';

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        minWidth: 0
    },
    filterBar: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1),
        backgroundColor: theme.palette.secondary.light,
        borderRadius: theme.shape.borderRadius
    },
    filterControl: {
        flex: '1 1 180px',
        minWidth: 0
    },
}));

const getColumnValue = (column, row) => column.valueGetter
    ? column.valueGetter(_.get(row, column.field), row)
    : _.get(row, column.field);

export default function DataCardList(props) {
    const {
        rows = [],
        columns = [],
        comparatorKeys = [],
        toolbarContent,
        cardRenderer,
        cardOptions = {},
        filter = false,
        filterOptions = {},
        mobileFilterByColumn,
        sort = false,
        paginate = false,
        paginateOptions = {},
        select = false,
        selectOptions = {},
        noRecordsText = 'Nenhum registro encontrado.'
    } = props;
    const classes = useStyles();
    const [filterValue, setFilterValue] = useState('');
    const [pendingFilterValue, setPendingFilterValue] = useState('');
    const [columnFilters, setColumnFilters] = useState({});
    const [page, setPage] = useState(paginateOptions.startPage || 0);
    const [rowsPerPage, setRowsPerPage] = useState(paginateOptions.rowsPerPage || 5);
    const [selected, setSelected] = useState(selectOptions.selectedRows?.[0] || null);

    useEffect(() => {
        const debounce = setTimeout(() => {
            setFilterValue(pendingFilterValue);
            setPage(0);
        }, 2000);

        return () => clearTimeout(debounce);
    }, [pendingFilterValue]);

    const filterableColumns = columns.filter(column => column.filterable !== false && column.rendered !== false);
    const sortableColumns = columns.filter(column => column.sortable && column.rendered !== false);
    const filterByColumn = mobileFilterByColumn === undefined
        ? filterOptions.filterByColumn
        : mobileFilterByColumn;

    const filteredRows = useMemo(() => rows.filter(row => {
        const searchableText = columns
            .filter(column => column.filterable !== false && column.rendered !== false)
            .map(column => {
                if (column.searchValue) return column.searchValue(row);

                const rawValue = _.get(row, column.field);
                const displayValue = getColumnValue(column, row);

                // Rendered React elements (for example, status badges) do not
                // contain searchable text, so use the underlying value instead.
                return React.isValidElement(displayValue) ? rawValue : displayValue;
            })
            .filter(value => value !== undefined && value !== null)
            .join(' ');

        const matchesText = !filterValue
            || searchableText.toUpperCase().includes(filterValue.toUpperCase());

        const matchesColumns = Object.entries(columnFilters).every(([field, value]) => {
            if (value === '' || value === undefined || value === null) return true;
            const column = columns.find(item => item.field === field);
            if (!column) return true;
            const rawValue = _.get(row, field);
            return column.customFilter
                ? column.customFilter(value, rawValue, row)
                : String(getColumnValue(column, row) ?? '').toUpperCase().includes(String(value).toUpperCase());
        });

        return matchesText && matchesColumns;
    }), [rows, columns, filterValue, columnFilters]);

    const pagedRows = paginate
        ? filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : filteredRows;

    useEffect(() => {
        if (!paginate || !filteredRows.length) {
            if (page !== 0) setPage(0);
            return;
        }

        const lastPage = Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);
        if (page > lastPage) setPage(lastPage);
    }, [filteredRows.length, page, paginate, rowsPerPage]);

    const onSelect = row => {
        if (!select) return;

        if (selectOptions.mode === 'single') {
            setSelected(row);
            selectOptions.onSelect && selectOptions.onSelect(row, null);
        }
    };

    const renderDefaultCard = (row, rowIndex) => {
        const cardColumns = cardOptions.fields || filterableColumns.filter(column => !column.cardAction);
        const actionColumns = columns.filter(column => column.cardAction && column.rendered !== false);
        const fields = cardColumns.slice(0, 2).map(column => ({
            label: column.label,
            value: getColumnValue(column, row)
        }));
        const detailFields = cardColumns.slice(2).map(column => ({
            label: column.label,
            value: getColumnValue(column, row)
        }));

        const actions = actionColumns
            .map(column => ({
                label: column.label,
                element: getColumnValue(column, row)
            }))
            .filter(action => action.element !== null
                && action.element !== undefined
                && action.element !== false);

        return (
            <CardCustomizado
                title={cardOptions.title ? cardOptions.title(row, rowIndex) : getColumnValue(cardColumns[0] || columns[0], row)}
                subtitle={cardOptions.subtitle && cardOptions.subtitle(row, rowIndex)}
                fields={fields}
                detailFields={detailFields}
                actions={actions}
                selected={selected === row}
                selectable={select && selectOptions.mode === 'single'}
                onSelect={() => onSelect(row)}
            />
        );
    };

    const renderCard = (row, rowIndex) => cardRenderer
        ? cardRenderer({
            row,
            rowIndex,
            selected: selected === row,
            onSelect: () => onSelect(row)
        })
        : renderDefaultCard(row, rowIndex);

    const onChangePage = (event, nextPage) => {
        setPage(nextPage);
        paginateOptions.onChangePage && paginateOptions.onChangePage(nextPage);
    };

    return (
        <div className={classes.root}>
            {!!toolbarContent && <ToolBar noBorderRadius>{toolbarContent}</ToolBar>}

            {filter && filterByColumn && (
                <div className={classes.filterBar} aria-label="Filtros">
                    {filterableColumns.map(column => (
                        column.filterOptions ? (
                            <FormControl variant="outlined" size="small" className={classes.filterControl} key={column.field}>
                                <InputLabel>{column.label}</InputLabel>
                                <Select
                                    value={columnFilters[column.field] || ''}
                                    onChange={event => {
                                        setColumnFilters(previous => ({...previous, [column.field]: event.target.value}));
                                        setPage(0);
                                    }}
                                    label={column.label}>
                                    <MenuItem value=""><em>Todos</em></MenuItem>
                                    {column.filterOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        ) : (
                            <TextField
                                key={column.field}
                                variant="outlined"
                                size="small"
                                label={column.label}
                                className={classes.filterControl}
                                value={columnFilters[column.field] || ''}
                                onChange={event => {
                                    setColumnFilters(previous => ({...previous, [column.field]: event.target.value}));
                                    setPage(0);
                                }}
                            />
                        )
                    ))}
                </div>
            )}

            {filter && !filterByColumn && (
                <ToolBar
                    search
                    onSearchChange={value => setPendingFilterValue(value)}
                    onSearch={value => {
                        setPendingFilterValue(value);
                        setFilterValue(value);
                        setPage(0);
                    }} />
            )}

            {sort && sortableColumns.length > 0 && (
                <div className={classes.filterBar}>
                    <FormControl variant="outlined" size="small" className={classes.filterControl}>
                        <InputLabel>Ordenar por</InputLabel>
                        <Select label="Ordenar por" defaultValue="">
                            <MenuItem value=""><em>Padrão</em></MenuItem>
                            {sortableColumns.map(column => <MenuItem key={column.field} value={column.field}>{column.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                </div>
            )}

            {!filteredRows.length && <Message type="info">{noRecordsText}</Message>}
            {!!filteredRows.length && pagedRows.map((row, index) => (
                <React.Fragment key={comparatorKeys.map(key => _.get(row, key)).join('|') || index}>
                    {renderCard(row, index)}
                </React.Fragment>
            ))}

            {paginate && filteredRows.length > 0 && (
                <MobilePagination
                    count={filteredRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    labelDisplayedRows={({from, to, count}) => `${from} até ${to} de ${count} registro(s)`}
                    onPageChange={onChangePage}
                />
            )}
        </div>
    );
}
